/*
 * 前端请求实例
 * @Author: Li Chunqiang
 * @Date: 2020-07-18 19:57:54
 * @Last Modified by: kouqi
 * @Last Modified time: 2021-02-26 11:31:39
 */
import { AxiosInstance } from 'axios';
import Cookies from 'js-cookie';
import { setDimensions } from '@utils/weblog';
// import kslog from '@utils/kslog';
import { Message } from '@ks/kwai-ui';
import { openLoginPage } from '@utils/permission/router';

import createAxios from './axios';

const getDeepValue = (data: any, keySquence: string[]) => {
    const len = keySquence.length;
    const reducer = (accumulator: any, key: string, idx: number) => {
        // eslint-disable-next-line
        return accumulator[key] ? accumulator[key] : idx === len - 1 ? undefined : {};
    };
    return keySquence.reduce(reducer, data || {});
};

export default function createClientAxios(
    param: {
        keys?: string;
        DEBUG?: boolean;
        showMessage?: boolean;
        timeout?: number;
        withCredentials?: boolean;
        // 接入ApiCenter后，原 status & code 判断状态已不适用，先新增 绿色通道 变量不检查，后续考虑重新封装axios
        expressWay?: boolean;
    } = {}
): AxiosInstance {
    const { keys, DEBUG, showMessage = true, timeout = 30 * 1000, withCredentials, expressWay = false } = param;
    const keyArr: string[] = keys ? (keys || '').split('.') : [];
    const customHeader: { [key: string]: any } = {};
    if (keyArr?.length) {
        const k = keyArr[keyArr.length - 1];
        const value = getDeepValue((window && window.__INITIAL_STATE__) || {}, keyArr);
        customHeader[k] = value;
    }
    if (DEBUG) {
        customHeader.DEBUG = 'DEBUG';
    }
    customHeader.shopId = localStorage.getItem('shopId') || ''; // TODO 临时兼容shopId
    const headers = {
        'x-csrf-token': Cookies.get('csrfToken'), // 从cookie中读取csrfToken，在请求头带上
        ...customHeader,
    };
    const axiosInstance: AxiosInstance = createAxios({
        headers,
        baseURL: '/',
        timeout,
        withCredentials,
    });
    // kslog.sendCountTag({ params: param });
    // 拦截请求并打点
    axiosInstance.interceptors.request.use(request => {
        const data = {
            type: 'request',
            status: 'info',
            msg: JSON.stringify(request.data),
            url: request.url,
            code: '',
        };
        // kslog.sendCountTag(data);
        return request;
    });

    axiosInstance.interceptors.response.use(
        response => {
            const { status, message, result, code, error_msg, msg, errMsg } = response.data;
            if (result === 109) {
                // 109; 处理登录失效和未登录;
                Message({
                    message: '登录失效，3s后自动跳转到登录页',
                    type: 'error',
                    onClose: () => {
                        openLoginPage();
                    },
                });
                const data = {
                    type: 'response',
                    status: 'error',
                    params: response.data,
                    msg: error_msg,
                    code: result,
                };
                // 上传到kslog
                // kslog.sendCountTag(data);
                return;
            }

            if (status === 401) {
                // 已经在无权限页面了，不需要跳转
                if (window.location.href.indexOf('admin/notfound') > -1) {
                    return;
                }
                // 应产品大哥要求，在数据中心没有权限的时候，直接跳转到无权限页面，不等待 3 秒
                if (window.location.href.indexOf('data-center') > -1) {
                    history.pushState({}, '', '/league/admin/notfound');
                    return;
                }
                // 401 无权限
                Message({
                    message: '暂无权限，3s后自动跳转到无权限页面',
                    type: 'error',
                    onClose: () => {
                        window.location.href = '/league/admin/notfound';
                    },
                });
                const data = {
                    type: 'response',
                    status: 'error',
                    params: response.data,
                    msg: error_msg,
                    code: status,
                };
                // 上传到kslog
                // kslog.sendCountTag(data);
                return;
            }
            if (
                !expressWay &&
                status !== 0 &&
                status !== 200 &&
                status !== 1 &&
                result !== 450011 &&
                result !== 1 &&
                code !== 200 &&
                code !== 1
            ) {
                showMessage &&
                    Message({
                        message: message || error_msg || msg || errMsg,
                        type: 'error',
                    });
                // 此处的 status 是业务的 status，状态码不为 200 时需要打点记录
                const data = {
                    type: 'response',
                    status: 'error',
                    msg: message,
                    url: response.config?.url,
                    code: status || result || code,
                    params: response.data,
                };
                // 上传到kslog
                // kslog.sendCountTag(data);
                return Promise.reject(response.data);
            }
            setDimensions({
                c_dimension1: response.config?.url,
                c_dimension2: status,
                c_dimension3: message,
            });
            return response.data;
        },
        error => {
            // 把具体的非200的错误信息透传显示
            const { response } = error;
            const message = (response && response.data && response.data.message) || '';
            setDimensions({
                c_dimension1: error.config?.url,
                c_dimension2: '999',
                c_dimension3: error.message,
            });
            Message({
                message: message || '服务器开小差了~',
                type: 'error',
            });
            const data = {
                type: 'response',
                status: 'error',
                msg: error?.message,
                url: error?.config?.url,
                code: error?.status,
                params: error,
            };
            // 上传到kslog
            // kslog.sendCountTag(data);
            return Promise.reject(error);
        }
    );

    return axiosInstance;
}
